import * as RadixDialog from "@radix-ui/react-dialog";
import type { ReactNode } from "react";

import { cn } from "./cn";

const overlayClassName = "fixed inset-0 z-40 bg-overlay";

/**
 * Slide-up on transform only. The global prefers-reduced-motion rule in
 * styles.css collapses transitions to opacity, so no per-component fallback.
 */
const sheetMotion =
  "translate-y-0 transition-transform motion-slow ease-out starting:translate-y-full";

function SheetHandle() {
  return (
    <span
      aria-hidden="true"
      data-testid="sheet-handle"
      className="mx-auto mb-3 block h-1.5 w-10 rounded-full bg-line"
    />
  );
}

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  /** Confirm / cancel row. Destructive commands always land here. */
  footer?: ReactNode;
}

/** Centred modal: confirmations and short decisions. */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
}: DialogProps) {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className={overlayClassName} />
        <RadixDialog.Content
          className={cn(
            "fixed left-1/2 top-1/2 z-50 w-[min(19.5rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2",
            "rounded-[28px] bg-raised p-5 shadow-sheet",
          )}
        >
          <RadixDialog.Title className="text-title text-fg">
            {title}
          </RadixDialog.Title>
          {description !== undefined && (
            <RadixDialog.Description className="mt-1 text-body text-fg-muted">
              {description}
            </RadixDialog.Description>
          )}
          {children && <div className="mt-3">{children}</div>}
          {footer && (
            <div className="mt-5 grid grid-flow-col auto-cols-fr gap-2.5">
              {footer}
            </div>
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

/** Bottom sheet: forms and selection. */
export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
}: DialogProps) {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className={overlayClassName} />
        <RadixDialog.Content
          className={cn(
            "fixed inset-x-0 bottom-0 z-50 max-h-[85vh] overflow-y-auto",
            "rounded-t-2xl bg-raised p-5 pb-[calc(env(safe-area-inset-bottom)+1.25rem)] shadow-sheet",
            sheetMotion,
          )}
        >
          <SheetHandle />
          <RadixDialog.Title className="text-title text-fg">
            {title}
          </RadixDialog.Title>
          {description !== undefined && (
            <RadixDialog.Description className="mt-1 text-body text-fg-muted">
              {description}
            </RadixDialog.Description>
          )}
          {children && <div className="mt-4">{children}</div>}
          {footer && (
            <div className="mt-5 flex flex-wrap justify-end gap-2.5">
              {footer}
            </div>
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

export interface SheetAction {
  id: string;
  label: string;
  onSelect: () => void;
  /** Destructive rows are drawn in the error colour and listed last. */
  danger?: boolean;
  /** The current destination: marked for assistive tech and in accent. */
  active?: boolean;
}

/** Action sheet: a titled list of one to four commands on one item. */
export function BottomSheet({
  open,
  onOpenChange,
  title,
  note,
  actions,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Non-actionable content between the title and the commands (e.g. a Badge). */
  note?: ReactNode;
  actions: SheetAction[];
}) {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className={overlayClassName} />
        <RadixDialog.Content
          aria-describedby={undefined}
          className={cn(
            "fixed inset-x-0 bottom-0 z-50 max-h-[85vh] overflow-y-auto",
            "rounded-t-2xl bg-raised pb-[env(safe-area-inset-bottom)] shadow-sheet",
            sheetMotion,
          )}
        >
          <div className="px-5 pb-3 pt-3">
            <SheetHandle />
            <RadixDialog.Title className="text-center text-callout font-semibold text-fg-muted">
              {title}
            </RadixDialog.Title>
            {note !== undefined && (
              <div className="mt-2 flex justify-center">{note}</div>
            )}
          </div>
          <ul>
            {actions.map((action) => (
              <li key={action.id}>
                <button
                  type="button"
                  aria-current={action.active ? "page" : undefined}
                  onClick={() => {
                    onOpenChange(false);
                    action.onSelect();
                  }}
                  className={cn(
                    "flex h-14 w-full items-center border-t border-line-subtle px-5 text-left text-body font-medium",
                    action.danger
                      ? "text-error"
                      : action.active
                        ? "font-semibold text-accent-fg"
                        : "text-fg",
                  )}
                >
                  {action.label}
                </button>
              </li>
            ))}
          </ul>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
